<template>
  <div class="catalog-view" :class="{ 'sidebar-collapsed': leftSidebarCollapsed }" v-if="canRead">
    <div class="left-sidebar">
      <div class="toggle-button d-flex justify-content-center align-items-center" @click="leftSidebarCollapsed = !leftSidebarCollapsed">
        <i class="fas fa-caret-right" v-if="leftSidebarCollapsed"></i>
        <i class="fas fa-caret-left" v-else></i>
      </div>
      <div class="catalog-name">
        <i class="fad fa-users-class"></i>
        <span>Grupos</span>
      </div>

      <!-- FILTERS AND VIEWS -->
      <div class="filters" v-if="currentAction === crudActions.READ">
        <div class="filter-items">
          <h3>Filtros de búsqueda</h3>
          <InputSelectorFilter
            v-if="canUseDomainFilter"
            :label="'Dominio'"
            :loadingData="loadingData"
            :defaultValue="domainDefaultValue"
            :values="domains"
            @update-value="updateDomain"
          />

          <div class="filter-item">
            <label for="filter-by-subgroup">Grupo</label>
            <b-form-input
              id="filter-by-subgroup"
              v-model="bySubgroup"
              type="search"
              placeholder="Buscar por ...">
            </b-form-input>
            <b-form-checkbox
              class="d-inline"
              id="checkbox-deep-search-subgroups"
              v-model="deepSearchSubgroups"
              size="sm"
              >Considerar subgrupos
            </b-form-checkbox>
            <div id="subgroups-check-info" class="d-inline ml-2"><i class="fal fa-info-circle inline"></i></div>
            <b-tooltip target="subgroups-check-info" triggers="hover">
              La búsqueda por nombre de grupo también se realizará en los subgrupos
            </b-tooltip>
          </div>

          <div class="filter-item">
            <label for="filter-by-user">Usuario</label>
            <b-form-input
              id="filter-by-user"
              v-model="byUser"
              type="search"
              placeholder="Buscar por usuario ...">
            </b-form-input>
            <b-form-checkbox
              class="d-inline"
              id="checkbox-deep-search-users"
              v-model="deepSearchUsers"
              size="sm"
              >Considerar subgrupos
            </b-form-checkbox>
            <div id="users-check-info" class="d-inline ml-2"><i class="fal fa-info-circle inline"></i></div>
            <b-tooltip target="users-check-info" triggers="hover">
              La búsqueda por nombre de usuario también se realizará en los subgrupos
            </b-tooltip>
          </div>

          <div class="filter-item">
            <label for="filter-by-project">Proyecto</label>
            <b-form-input
              id="filter-by-project"
              v-model="byProject"
              type="search"
              placeholder="Buscar por proyecto ...">
            </b-form-input>
            <b-form-checkbox
              class="d-inline"
              id="checkbox-deep-search-projects"
              v-model="deepSearchProjects"
              size="sm"
              >Considerar subgrupos
            </b-form-checkbox>
            <div id="projects-check-info" class="d-inline ml-2"><i class="fal fa-info-circle inline"></i></div>
            <b-tooltip target="projects-check-info" triggers="hover">
              La búsqueda por nombre de proyecto también se realizará en los subgrupos
            </b-tooltip>
          </div>

          <div class="filter-item">
            <!-- Continuar trabajando con el filtro por estado -->
            <b-form-checkbox
              v-model="activeGroups"
              :value="groupStatus.ACTIVE"
              :unchecked-value="groupStatus.INACTIVE">
              Solo grupos activos
            </b-form-checkbox>
            <label>Desde - Hasta</label>
            <DateSelector
              :disabled="activeGroups === groupStatus.INACTIVE"
              range
              format="DD/MM/YYYY"
              :range-separator="' - '"
              :disabled-date="notIn31DaysRange"
              :editable="false"
              :clearable="true"
              v-model="fromToDates"
              placeholder="dd/mm/aaaa - dd/mm/aaaa"/>
          </div>
        </div>

        <b-row class="main-actions">
          <b-overlay
            class="main-actions__overlay"
            spinner-type="grow"
            spinner-small
            :show="loadingData">
            <b-col cols="6">
              <b-button block size="sm" class="shadow-sm" @click="cleanFilter" :disabled="loadingData">Limpiar</b-button>
            </b-col>
            <b-col cols="6">
              <b-button variant="primary" block size="sm" class="shadow-sm" @click="filterItems" :disabled="loadingData">Buscar</b-button>
            </b-col>
          </b-overlay>
        </b-row>
      </div>

      <div class="short-list" v-if="currentAction !== crudActions.READ">
        <h5>Registros:</h5>
        <b-list-group>
          <b-list-group-item
            class="text-truncate"
            v-for="item in filteredItems"
            :title="item.id + ' - ' + item.name"
            :key="item.id"
            :active="selectedItem == item"
            @click="selectItem(item)">
            {{ item.name }}
          </b-list-group-item>
        </b-list-group>
      </div>
    </div>

    <div class="right-side">
      <div class="grid">
        <!-- TOP BUTTONS BAR -->
        <div class="top-tool-bar">
          <div class="action d-none d-lg-block" @click="newItem" v-if="canCreate"><i class="fal fa-file-plus"></i> Nuevo</div>
          <div class="action d-none d-lg-block" @click="queryItem" :class="{ disabled: !selectedItem.id }"><i class="fal fa-print-search"></i> Detalle</div>
          <div class="action d-none d-lg-block" @click="updateItem" v-if="canUpdate" :class="{ disabled: !selectedItem.id || selectedItem.status === groupStatus.INACTIVE }"><i class="fal fa-file-edit"></i> Modificar</div>
          <div class="action d-none d-lg-block"
            @click="showDisplayModal" v-if="canDelete"
            :class="{ disabled: !selectedItem.id || selectedItem.status === groupStatus.INACTIVE }">
            <i class="fal fa-trash-alt"></i> Desactivar</div>
          <div class="action d-none d-lg-block" title="Descargar registros" @click="printCsv"><i class="fal fa-file-spreadsheet"></i> Exportar</div>
          <div class="action d-none d-lg-block" title="Refrescar tabla para obtener las últimas actualizaciones" @click="loadData"><i class="fal fa-sync"></i> Refrescar</div>
          <ImportFile v-if="importGroups"
            :actionName="'Importar Grupos'"
            :entityName="'Grupos'"
            :fields="importGroupsFields"
            :entityFields="groupImportDetails"
            :prepareEntity="createGroupToImport"
            :saveEntity="saveGroup"
            :updateEntity="updateGroupByImport"
            @reload-data="loadData"/>

          <!-- MISMOS BOTONES PERO EN TAMAÑO REDUCIDO PARA PANTAÑAS PEQUEÑAS -->
          <div class="action d-lg-none d-sm-block" @click="newItem" v-if="canCreate"><i class="fal fa-file-plus"></i></div>
          <div class="action d-lg-none d-sm-block" @click="queryItem" :class="{ disabled: !selectedItem.id }"><i class="fal fa-print-search"></i> </div>
          <div class="action d-lg-none d-sm-block" @click="updateItem" v-if="canUpdate" :class="{ disabled: !selectedItem.id || selectedItem.status === groupStatus.INACTIVE }"><i class="fal fa-file-edit"></i></div>
          <div class="action d-lg-none d-sm-block"
            @click="showDisplayModal" v-if="canDelete"
            :class="{ disabled: !selectedItem.id || selectedItem.status === groupStatus.INACTIVE }">
            <i class="fal fa-trash-alt"></i></div>
          <div class="action d-lg-none d-sm-block" title="Descargar registros" @click="printCsv"><i class="fal fa-file-spreadsheet"></i></div>
          <div class="action d-lg-none d-sm-block" title="Refrescar tabla para obtener las últimas actualizaciones" @click="loadData"><i class="fal fa-sync"></i></div>
          <ImportFile v-if="importGroups"
            class="d-lg-none"
            :sizeClass="'d-sm-block'"
            :actionName="''"
            :entityName="'Grupos'"
            :fields="importGroupsFields"
            :entityFields="groupImportDetails"
            :prepareEntity="createGroupToImport"
            :saveEntity="saveGroup"
            :updateEntity="updateGroupByImport"
            @reload-data="loadData"/>
        </div>

        <!-- DATA TABLE -->
        <div class="table-container">
          <div class="list" v-if="filteredItems.length">
            <GroupListItem
              v-for="item in filteredItems"
              :key="item.id"
              :group="item"
              :root="true"
              :opened="item.subGroups.length === 0"
              :selectedId="selectedItem.id"
              @select-open="selectGroupAndDetails"
              @select="selectGroup"/>
          </div>
          <div v-else>
            Sin registros encontrados
          </div>
        </div>
      </div>

      <!-- CONSULTA DE DETALLE -->
      <transition name="fade-in-right">
        <div class="detail" v-if="currentAction === crudActions.READ_DETAIL">
          <AdminGroupForm :id="selectedItem.id" :key="selectedItem.id" :readOnly="true" :title="'Grupo: ' + selectedItem.name" @close="onCancelForm" @modify-item="modifyItem" @delete-item="showDisplayModal"/>
        </div>
      </transition>

      <!-- NUEVO REGISTRO -->
      <transition name="fade-in-right">
        <div class="detail" v-if="currentAction === crudActions.CREATE">
          <AdminGroupForm :id="selectedItem.id" :key="selectedItem.id" title="Nuevo Grupo" @close="currentAction = crudActions.READ" @saved="onSaveItem" :canAddUsers="canReadUsers" :canReadProjects="canReadProjects"/>
        </div>
      </transition>

      <!-- ACTUALIZACIÓN -->
      <transition name="fade-in-right">
        <div class="detail" v-if="currentAction === crudActions.UPDATE">
          <AdminGroupForm :id="selectedItem.id" :key="selectedItem.id" :title="'Modificando: ' + selectedItem.name" @close="currentAction = crudActions.READ" @saved="onSaveItem" :canAddUsers="canReadUsers" :canReadProjects="canReadProjects"/>
        </div>
      </transition>

      <!-- MODAL GROUP DELETE -->
      <b-modal
        id="delete-group-modal"
        scrollable
        header-bg-variant="danger"
        header-text-variant="white"
        no-close-on-esc
        no-close-on-backdrop
        hide-header-close>
        <div>
          <p>Estás por desactivar el grupo:</p>
          <div class="group--delete">
            <span class="group-id">ID:{{ selectedItem.id }} <i class="fad fa-users-class"></i></span>
            <p><b>Grupo:</b> {{ selectedItem.name }}</p>
            <p><b>Estado:</b> {{ selectedItemStatus }}</p>
            <p><b>Última actualización:</b> {{ selectedItem.updateDate }}</p>
          </div>
          <p>
            Esta desactivación es una eliminación lógica, por lo cual, cambia el estado del grupo a <em>Inactivo</em>.
            Una vez inactivo ya no será posible modificar ni relacionar con otros grupos.
          </p>
          <div class="delete-action-confirm">
            Favor de marca la casilla para confirmar
            <b-checkbox v-model="confirmDeleteAction">Sí, estoy seguro</b-checkbox>
          </div>
        </div>
        <template v-slot:modal-title>
          <div class="inline">
            <i class="fal fa-trash-alt"></i>
            Desactivación de grupo
          </div>
        </template>
        <template v-slot:modal-footer>
          <b-button variant="light" @click="closeDeleteModal">
            Cancelar
          </b-button>
          <b-button variant="danger" :disabled="!confirmDeleteAction" @click="deleteItem">
            Eliminar
          </b-button>
        </template>
      </b-modal>

      <!-- LOADING MASK -->
      <div class="loading-mask" v-show="loadingData">
        <i class="far fa-spinner-third fa-spin"></i>
      </div>
    </div>

    <a class="hidden-csv-download-link" ref="csvDownload"></a>
  </div>
  <div v-else>
    <div id="not-sufficent-permissions" class="page-wrap d-flex flex-row align-items-center">
        <div class="container">
          <div class="row justify-content-center">
              <div class="col-md-12 text-center">
                  <span class="display-1 d-block">403</span>
                  <div class="mb-4 lead">Lo sentimos, no tienes permisos suficientes para consultar estos registros.</div>
              </div>
          </div>
        </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Vue } from 'vue-property-decorator'
import AdminGroupForm from '@/components/admin/AdminGroupForm.vue'
import TopToolBarPaginator from '@/components/generic/TopToolBarPaginator.vue'
import { MessageError } from '@/components/generic/MessageHandling.vue'
import GroupListItem from '@/components/generic/GroupListItem.vue'
import catalogsService from '@/services/CatalogsService'
import userInfoStore from '@/store/userInfo'
import { BasicEntity, CrudActions, DomainCatalogFilters } from '@/types/Catalogs'
import { GroupCatalogFilters, GroupEntity, GroupStatus } from '@/types/catalogs/Groups'
import { PageRequest } from '@/types/Support'
import { PermissionNames } from '@/types/Auth'
import reportsService from '@/services/ReportsService'
import moment from 'moment'
import { ErrorImport, ImportErrorCode } from '@/types/Errors'
import { ErrorTitles, ImportationErrorAdvices } from '@/types/ErrorDictionary'
import ImportFile from '@/components/generic/ImportFile.vue'
import InputSelectorFilter from '@/components/generic/InputSelectorFilter.vue'
import DateSelector from '@/components/generic/DateSelector.vue'
import LogService, { ToString } from '@/services/support/LogService'

@Component({
  components: {
    TopToolBarPaginator,
    GroupListItem,
    MessageError,
    AdminGroupForm,
    ImportFile,
    InputSelectorFilter,
    DateSelector
  }
})
export default class AdminGroups extends Vue {
  PAGE_SIZE = 500
  paginatorKey = 0
  crudActions = CrudActions
  currentAction = CrudActions.READ
  loadingData = false
  leftSidebarCollapsed = false
  items: GroupEntity[] = []
  filteredItems: GroupEntity[] = []
  /** Filters */
  selectedItem: GroupEntity = new GroupEntity()
  bySubgroup: string = ''
  deepSearchSubgroups: boolean = false
  byUser: string = ''
  deepSearchUsers: boolean = false
  byProject: string = ''
  deepSearchProjects: boolean = false
  selectedItemId: number = 0
  loadingDomains: boolean = false
  domains: BasicEntity[] = []
  domainDefaultValue: BasicEntity = new BasicEntity(-1, '')
  activeGroups: GroupStatus = GroupStatus.INACTIVE
  fromToDates: Date[] = []
  confirmDeleteAction: boolean = false
  groupStatus = GroupStatus

  creationDate = ''
  updateDate = ''

  importGroupsFields: any[] = [
    { key: 'id', label: 'ID' },
    { key: 'name', label: 'Nombre' },
    { key: 'domain', label: 'Dominio' }
  ]

  mounted () {
    if (this.canUseDomainFilter) {
      this.setDomainValue(userInfoStore.userInfo.domain)
      let pageRequest = this.prepareDomainRequest()
      this.loadDomains(pageRequest)
    }
    this.loadData()
  }

  /* User permissions */

  /**
   * Checks if the user has 'permission' on its permissions
   */
  hasPermissions (permissions: PermissionNames[]) {
    return permissions.every(p => userInfoStore.hasPermission(p))
  }

  get canRead () {
    return this.hasPermissions([PermissionNames.GROUPS_READ])
  }

  get canCreate () {
    return this.hasPermissions([PermissionNames.GROUPS_CREATE])
  }

  get canUpdate () {
    return this.hasPermissions([PermissionNames.GROUPS_UPDATE])
  }

  get canDelete () {
    return this.hasPermissions([PermissionNames.GROUPS_DELETE])
  }

  get canReadUsers () {
    return this.hasPermissions([PermissionNames.USERS_READ])
  }

  get canReadProjects () {
    return this.hasPermissions([PermissionNames.PROJECTS_READ])
  }

  get importGroups () {
    return this.hasPermissions([PermissionNames.GROUPS_IMPORT, PermissionNames.GROUPS_CREATE, PermissionNames.GROUPS_UPDATE])
  }
  /** @returns true if the user can use the domain filter, false otherwise. */
  get canUseDomainFilter () {
    return this.hasPermissions([PermissionNames.DOMAINS_READ]) && userInfoStore.inRootDomain()
  }

  get selectedItemStatus () {
    return this.selectedItem.id > 0 ? GroupEntity.getStatus(this.selectedItem) : 'Sin estado'
  }

  /**
   * Prepares a page request, if the user belongs to 'root' domain,
   * query parameter 'domain' is set it from cookie 'domain-3.1',
   */
  preRequest () {
    let pageRequest = new PageRequest<GroupCatalogFilters>(this.PAGE_SIZE)
    pageRequest.filters.loadRelations = true
    pageRequest.filters.status = this.activeGroups
    pageRequest.filters.fromToDates = this.fromToDates
    if (this.canUseDomainFilter) {
      pageRequest.filters.domain = this.domainDefaultValue.id
    }
    return pageRequest
  }

  /**
   * Clears and loads all groups records
   */
  async loadData () {
    if (this.loadingData) return
    this.cleanItemSelected()
    this.loadingData = true
    this.items = []
    let pageRequest = this.preRequest()
    await this.loadGroups(pageRequest)
  }

  /**
   * Get all group records and assign them in 'items' variable
   * @param pageRequest - configurations to request
   */
  async loadGroups (pageRequest: PageRequest<GroupCatalogFilters>) {
    this.loadingData = true
    await catalogsService.getGroups(pageRequest).then((response) => {
      this.items = response.results
      this.filteredItems = this.items
    }).catch((error) => {
      let errorProps = {
        title: 'Error al cargar grupos.',
        message: '',
        error: error,
        delay: 5000
      }
      new MessageError({ propsData: errorProps }).displayMessage()
    }).finally(() => {
      this.loadingData = false
    })
  }

  /**
   * Gets the domain records and assign them to the 'domains' variable.
   *
   * @param pageRequest configurations for the domains request.
   */
  loadDomains (pageRequest: PageRequest<DomainCatalogFilters>) {
    this.loadingDomains = true
    catalogsService.getDomains(pageRequest)
      .then((response) => {
        this.domains.push(
          ...response.results.map((domain) => {
            return new BasicEntity(domain.id, domain.name)
          }))

        if (this.domains.length < response.total) {
          pageRequest.page++
          this.loadDomains(pageRequest)
        }
      })
      .catch((error) => {
        let errorProps = {
          title: 'Error al cargar dominios.',
          message: '',
          error: error,
          delay: 5000
        }
        new MessageError({ propsData: errorProps }).displayMessage()
      })
      .finally(() => { this.loadingDomains = false })
  }

  /**
   * Sets the domain filter with the value selected
   * and reloads the records with that domain param
   *
   * @param domain selected
   */
  updateDomain (domain: BasicEntity) {
    if (domain.id !== this.domainDefaultValue.id) {
      this.setDomainValue(domain)
      this.loadData()
    }
  }
  /** @returns a page request with the configurations to get the domain records */
  prepareDomainRequest () {
    let pageRequest = new PageRequest<DomainCatalogFilters>(this.PAGE_SIZE)
    pageRequest.sort = 'name'
    return pageRequest
  }
  /**
   * Sets the domain filter value with the 'domain' received as param.
   *
   * @param domain to set as domain in the filter
   */
  setDomainValue (domain: BasicEntity) {
    this.domainDefaultValue = domain
  }
  /**
   * Select the current item and assign it in 'selectedItem' variable.
   * @param item - row selected on table
   */
  selectItem (item: any) {
    switch (this.currentAction) {
      case CrudActions.READ:
      case CrudActions.READ_DETAIL:
      case CrudActions.UPDATE:
      case CrudActions.DELETE:
        this.selectedItem = item
        break
      case CrudActions.CREATE:
        this.selectedItem = this.hasPermissions([PermissionNames.GROUPS_CREATE]) ? item : this.selectedItem
        this.currentAction = CrudActions.UPDATE
        break
    }
  }

  /**
   * Opens record on double clic
   * @param group to open
   */
  selectGroupAndDetails (group: GroupEntity) {
    this.selectGroup(group)
    this.queryItem()
  }

  /**
   * Last selected group on tree groups
   * @param group - group selected
   */
  selectGroup (group: GroupEntity) {
    if (this.selectedItemId !== group.id) {
      this.selectedItem = group
      this.selectedItemId = group.id
    }
  }

  /**
   * Resets selected item values
   */
  cleanItemSelected () {
    this.selectedItem = new GroupEntity()
    this.selectedItemId = -1
  }

  /**
   * Sets current action to read details
   */
  queryItem () {
    if (this.selectedItem.id) {
      this.currentAction = CrudActions.READ_DETAIL
    }
  }

  /**
   * Sets current action to create a record
   */
  newItem () {
    this.selectedItem = new GroupEntity()
    this.currentAction = CrudActions.CREATE
  }

  /**
   * Sets current action to update a record
   */
  updateItem () {
    if (this.selectedItem.id) {
      this.currentAction = CrudActions.UPDATE
    }
  }

  /**
   * Handles delete records flow
   * Launchs a message confirmation and calls delete API record
   */
  deleteItem () {
    catalogsService.deleteGroup(this.selectedItem.id).then((response) => {
      let message = `El grupo ${this.selectedItem.id} - ${this.selectedItem.name} se ha eliminado correctamente`
      this.$bvToast.toast(message, {
        title: 'Eliminación finalizada',
        variant: 'success',
        toaster: 'b-toaster-top-center',
        solid: true,
        autoHideDelay: 4000
      })
    }).catch((error) => {
      let messageValues = {
        title: 'Error al eliminar el grupo',
        message: '',
        delay: 10000,
        error: error
      }
      new MessageError({ propsData: messageValues }).displayMessage()
    }).finally(() => {
      this.loadData()
      this.closeDeleteModal()
      this.currentAction = CrudActions.READ
    })
  }

  /** Shows the modal message to confirm the action */
  showDisplayModal () {
    this.$bvModal.show('delete-group-modal')
  }

  /** Hides the modal message */
  closeDeleteModal () {
    this.setConfirmationToDelete(false)
    this.$bvModal.hide('delete-group-modal')
  }

  setConfirmationToDelete (value: boolean) {
    this.confirmDeleteAction = value
  }

  /**
   * Sets the available dates for 'from' and 'to' dates
   * in DateSelector for status param
   *
   * @param date - in calendar
   * @param current - selected date in calendar
   */
  notIn31DaysRange (date: Date, current: Date[]) {
    let notInRange = false
    let today = moment().toDate()
    if (current.length > 0) {
      let minRangeDate = moment(current[0]).add(-31, 'days')
      let maxRangeDate = moment(current[0]).add(31, 'days')
      notInRange = date < minRangeDate.toDate() || date > maxRangeDate.toDate()
    }
    return date > today || notInRange
  }

  /**
   * Update current action and reloads records.
   */
  onSaveItem () {
    this.currentAction = CrudActions.READ
    this.cleanItemSelected()
    this.loadData()
  }

  /**
   * Switch to edit mode
   */
  modifyItem () {
    this.currentAction = CrudActions.UPDATE
  }

  /** Switch to read mode */
  onCancelForm () {
    this.currentAction = CrudActions.READ
  }

  /** Filter and sorting */

  /**
   * Clear current filter configurations
   */
  cleanFilter () {
    this.filteredItems = this.items
    this.bySubgroup = ''
    this.byUser = ''
    this.byProject = ''
    this.deepSearchSubgroups = false
    this.deepSearchUsers = false
    this.deepSearchProjects = false
    this.activeGroups = this.groupStatus.INACTIVE
    this.fromToDates = []
    this.cleanItemSelected()
    this.filterItems()
  }

  /**
   * Apply all filters
   */
  async filterItems () {
    await this.loadData()
    this.loadingData = true
    this.filteredItems = this.items
    const consideredGroupIds: number[] = []
    if (this.bySubgroup || this.byUser || this.byProject) this.filteredItems = []
    if (this.bySubgroup) {
      for (let group of this.items) {
        this.searchByGroup(group, this.bySubgroup, consideredGroupIds, this.deepSearchSubgroups)
      }
    }
    if (this.byUser) {
      for (let group of this.items) {
        this.searchByUser(group, this.byUser, consideredGroupIds, this.deepSearchUsers)
      }
    }
    if (this.byProject) {
      for (let group of this.items) {
        this.searchByProject(group, this.byProject, consideredGroupIds, this.deepSearchProjects)
      }
    }
    this.loadingData = false
  }

  /**
   * Adds the group if some group or subgroup name matches with 'groupName'
   * Handles direct search or deep search (consider subgroups if flag are active)
   * @param group - to iter on its subgroups
   * @param groupName - name to match
   * @param idsConsidered - list of ids to avoid repeat items
   * @param deepSearh - to considered subgroups
   */
  searchByGroup (group: GroupEntity, groupName: string, idsConsidered: number[], deepSearch: boolean) {
    let regexSearch = new RegExp(groupName, 'i')
    if (regexSearch.test(group.name) && !idsConsidered.includes(group.id)) {
      this.filteredItems.push(group)
      idsConsidered.push(group.id)
    }
    if (deepSearch) {
      for (let subgroup of group.subGroups) {
        this.searchByGroup(subgroup, groupName, idsConsidered, deepSearch)
      }
    }
  }

  /**
   * Adds the group if some project name matches with 'userName'
   * Handles direct search or deep search (consider subgroups if flag are active)
   * @param group - to iter on its users
   * @param groupName - name to match
   * @param idsConsidered - list of ids to avoid repeat items
   * * @param deepSearh - to considered subgroups users
   */
  searchByUser (group: GroupEntity, userName: string, idsConsidered: number[], deepSearch: boolean) {
    let regexSearch = new RegExp(userName, 'i')
    if (!idsConsidered.includes(group.id) && group.users) {
      for (let user of group.users) {
        if (regexSearch.test(user.name)) {
          this.filteredItems.push(group)
          idsConsidered.push(group.id)
        }
      }
    }
    if (deepSearch) {
      for (let subgroup of group.subGroups) {
        this.searchByUser(subgroup, userName, idsConsidered, deepSearch)
      }
    }
  }

  /**
   * Adds the group if some project name matches with 'projectName'
   * Handles direct search or deep search (consider subgroups if flag are active)
   * @param group - to iter on its projects
   * @param groupName - name to match
   * @param idsConsidered - list of ids to avoid repeat items
   * * @param deepSearh - to considered subgroups projects
   */
  searchByProject (group: GroupEntity, projectName: string, idsConsidered: number[], deepSearch: boolean) {
    let regexSearch = new RegExp(projectName, 'i')
    if (!idsConsidered.includes(group.id) && group.projects) {
      for (let project of group.projects) {
        if (regexSearch.test(project.name)) {
          this.filteredItems.push(group)
          idsConsidered.push(group.id)
        }
      }
    }
    if (deepSearch) {
      for (let subgroup of group.subGroups) {
        this.searchByProject(subgroup, projectName, idsConsidered, deepSearch)
      }
    }
  }

  /**
   * Generates string csv format for a group entity, consider:
   * id, name, domain, subgroups list, users list, projects list, cration date and update date
   *
   * @param group entity to work with
   * @param level of group, where 0 is a root group
   */
  printGroupInfo (group: GroupEntity, alreadyIds: number[]) : String {
    let info = ''
    info += `${reportsService.formatTextForCsvFile(group.id.toString())},`
    info += `${reportsService.formatTextForCsvFile(group.name)},`
    info += `${reportsService.formatTextForCsvFile(group.domain.name)},`
    info += group.subGroups && group.subGroups.length ? group.subGroups.reduce((arr: string[], it: GroupEntity) => arr.concat(it.name), []).join('/').concat(',') : ','
    info += group.users && group.users.length ? group.users.reduce((arr: string[], it: BasicEntity) => arr.concat(it.name), []).join('/ ').concat(',') : ','
    info += group.projects && group.projects.length ? group.projects.reduce((arr: string[], it: BasicEntity) => arr.concat(it.name), []).join('/ ').concat(',') : ','
    info += `${moment(group.creationDate).format('DD/MM/YYYY')},`
    info += `${moment(group.updateDate).format('DD/MM/YYYY')}`
    for (let subgroup of group.subGroups) {
      if (!alreadyIds.includes(subgroup.id)) {
        alreadyIds.push(subgroup.id)
        info += '\n'
        info += this.printGroupInfo(subgroup, alreadyIds)
      }
    }
    return info
  }

  /**
   * Downloads csv file with all group records
   */
  printCsv () {
    let csv = 'ID,NOMBRE,DOMINIO,SUBGRUPOS,USUARIOS,PROYECTOS,CREACION,ACTUALIZACION\n'

    let alreadyIds: number[] = []
    for (let it of this.filteredItems) {
      if (!alreadyIds.includes(it.id)) {
        alreadyIds.push(it.id)
        csv += this.printGroupInfo(it, alreadyIds) + '\n'
      }
    }

    let element = this.$refs.csvDownload as any
    element.href = 'data:text/csv;charset=utf-8,' + encodeURI(csv)
    element.target = '_blank'
    element.download = `grupos.csv`
    element.click()
  }

  /** Import groups */
  /**
   * Obtains the values to display as details on import file action.
   * This values must be the same specified on 'importGroupsFields' variable
   *
   * @returns object with the column values to use on import file action
   */
  groupImportDetails (group: GroupEntity) {
    return {
      id: group.hasOwnProperty('id') ? group.id : '',
      name: group.name,
      domain: group.domain.name
    }
  }
  /**
   * Creates a group instance based on the values that are received
   * as parameter
   *
   * @param groupValues - a object with group values to use
   * @returns  a group instance
   * @throws an ErrorImport when the group values are incomplete
   *  or if have an incorrect format.
   */
  async createGroupToImport (groupValues: any) {
    try {
      let group = GroupEntity.getInstanceForEdit()

      group.name = this.getProperty(groupValues, 'Nombre')
      // Assign the domain entity
      let domainString = this.getProperty(groupValues, 'DominioId')
      await this.setDomainToGroup(group, domainString)

      // Assign the subgroup entities
      let subgroupIds = this.getPropertyList(groupValues, 'SubgruposIds')
      this.setSubgroupToGroup(group, subgroupIds)

      // Assign the user entities (type BasicEntity)
      let userIds = this.getPropertyList(groupValues, 'UsuariosIds')
      this.setUsersToGroup(group, userIds)

      // Assign the project entities (type BasicEntity)
      let projectIds = this.getPropertyList(groupValues, 'ProyectosIds')
      this.setProjectsToGroup(group, projectIds)

      let missingRequiredValues = group.validatesRequiredValues()

      if (missingRequiredValues.length) {
        let advices: string[] = []
        advices = ImportationErrorAdvices.no_required_group_values
        advices.push(`Valores faltantes: ${missingRequiredValues}`)
        throw ErrorImport.getErrorRequiredValues(advices)
      } else {
        let formatErrors = group.validateFormatProperties()
        if (formatErrors.length) {
          throw ErrorImport.getErrorFormatValues(formatErrors, group.name)
        }
      }

      return group
    } catch (error) {
      LogService.error('Error durante la preparación de un grupo.')
      throw error
    }
  }
  /**
   * Based on the 'group' received as parameter, searchs the item
   * by users api and updates its values.
   *
   * @param group values to update
   * @returns a user object to save
   * @throws an ErrorImport if the group does not exist or
   *  if the group does not have required values or have a incorrect format.
   */
  async updateGroupByImport (groupValues: any) {
    try {
      let groupEntity = new GroupEntity()
      // Validates the user existence
      await catalogsService.getGroupById(groupValues.id)
        .then((groupResponse) => {
          groupEntity.id = groupResponse.id
          groupEntity.name = groupResponse.name
          groupEntity.domain = groupResponse.domain
          groupEntity.subGroups = groupResponse.subGroups
          groupEntity.users = groupResponse.users
          groupEntity.projects = groupResponse.projects
        })
        .catch((error) => {
          LogService.error(`Error en la consulta de un grupo: ${ToString(error.response ?? error)}`)
          throw new ErrorImport(
            ImportErrorCode.NO_RELATED_ENTITY,
            ErrorTitles.group_not_found,
            `El grupo con el ID: ${groupValues.id} no se ha podido cargar.`,
            ImportationErrorAdvices.entity_not_found
          )
        })

      let subgroupsForUpdate = this.getPropertyList(groupValues, 'SubgruposIds')
      this.setSubgroupToGroup(groupEntity, subgroupsForUpdate)

      let usersForUpdate = this.getPropertyList(groupValues, 'UsuariosIds')
      this.setUsersToGroup(groupEntity, usersForUpdate)

      let projectsForUpdate = this.getPropertyList(groupValues, 'ProyectosIds')
      this.setProjectsToGroup(groupEntity, projectsForUpdate)

      let missingRequiredValues = groupEntity.validatesRequiredValues()
      if (missingRequiredValues.length) {
        throw ErrorImport.getErrorRequiredValues([
          `Lo siguientes valores son necesarios para poder actualizar: ${missingRequiredValues},
          Usuario: ${groupEntity.id} - ${groupEntity.name}`
        ])
      } else {
        let errorFormatValues = groupEntity.validateFormatProperties()
        if (errorFormatValues.length) {
          throw ErrorImport.getErrorFormatValues(
            errorFormatValues,
            groupEntity.name
          )
        }
      }
      return groupEntity
    } catch (error) {
      LogService.error(`Error durante la actualización de valores de un grupo.`)
      throw error
    }
  }
  /**
   * Gets the save group function from catalogService
   * to use in import component
   */
  get saveGroup () {
    return catalogsService.saveGroup
  }
  /** Gets the domain with the 'domainid' as Id by API */
  getDomainById (domainId : number) {
    return catalogsService.getDomainById(domainId)
  }
  /**
   * Based on the domainId, it is search by the API
   * and assign it to the group.
   *
   * @param group to set the domain
   * @param domainId to search and assign it, if this does exist
   *  the a new BasicEntity instance is assign it.
   */
  async setDomainToGroup (group: GroupEntity, domainId: string) {
    let domainResponse =
      domainId.toString().length
        ? await this.getDomainById(parseInt(domainId)) as BasicEntity
        : { id: -1, name: '' } as BasicEntity
    group.domain = domainResponse
  }
  /**
   * Based on a 'subgroupIds' list, creates as many group entities
   * as ids in the list and assigns it as the subgroup list for the 'group'
   *
   * @param group to assign subgroup list
   * @param subgroupIds to creates groups
   */
  setSubgroupToGroup (group: GroupEntity, subgroupIds: string[]) {
    if (subgroupIds.length) {
      group.subGroups = subgroupIds.map((id: string): GroupEntity => {
        let group = GroupEntity.getInstanceForEdit()
        group.id = this.isIntNotNaN(id)
        return group
      })
    }
  }
  /**
   * Based on a 'userIds' list, creates as many basic entities
   * as ids in the list and assigns it as the users list for the 'group'
   *
   * @param group to assign subgroup list
   * @param usersIds to creates users
   */
  setUsersToGroup (group: GroupEntity, userIds: string[]) {
    if (userIds.length) {
      group.users = userIds.map((id: string): BasicEntity => {
        let user = new BasicEntity(-1, '')
        user.id = this.isIntNotNaN(id)
        return user
      })
    }
  }
  /**
   * Based on a 'projectIds' list, creates as many basic entities
   * as ids in the list and assigns it as the project list for the 'group'
   *
   * @param group to assign subgroup list
   * @param projectIds to creates projects
   */
  setProjectsToGroup (group: GroupEntity, projectIds: string[]) {
    if (projectIds.length) {
      group.projects = projectIds.map((id: string): BasicEntity => {
        let proyect = new BasicEntity(-1, '')
        proyect.id = this.isIntNotNaN(id)
        return proyect
      })
    }
  }
  /**
   * Validates if the string can be parse into number
   * and is not NaN
   *
   * @param string to validates
   * @returns the value if pass the test, otherwise -1
   */
  isIntNotNaN (string: string) {
    let value = parseInt(string.trim())
    if (!isNaN(value)) {
      return value
    }
    return -1
  }
  /**
   * Validates if the object has a determinate property and returns it
   *
   * @param object to validate
   * @returns the property value if exist, otherwise null
   */
  getProperty (object: any, property: string) {
    return object.hasOwnProperty(property) ? object[property] : undefined
  }
  /**
   * Validates if the object has a determinate list property and returns it
   *
   * @param object to validate
   * @returns the property value if exist, otherwise an empty array
   */
  getPropertyList (object: any, property: string): string[] {
    if (object.hasOwnProperty(property)) {
      let stringProperty = object[property].toString()
      let propertyValue = stringProperty.includes(',')
        ? object[property].split(',')
        : [object[property].toString()]
      return propertyValue
    }
    return []
  }
}
</script>

<style lang="scss" scoped>
$gray-200: #e9ecef;
.group--delete {
  position: relative;
  padding: 0.5rem 1rem;
  border-radius: 0 8px 8px 0;
  background-color: $gray-200;
  box-shadow: -4px 0 #000;
}
.group--delete > p {
  margin: 0;
}
.group--delete > .group-id {
  display: inline-block;
  position: absolute;
  top: 0.5rem;
  right: 1rem;
  font-size: 1.2rem;
  font-weight: 600;
}
</style>
